/**
 * BetProtect — Iterative User Learning Engine
 *
 * Tracks check-in history (mood, urge, triggers) and exercise completions/ratings
 * to build a personalised profile that improves suggestions over time.
 *
 * All data is stored in localStorage (client-side only, no external service needed).
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CheckInRecord {
  id: string;
  timestamp: number; // Unix ms
  mood: number; // 1–5
  urge: number; // 1–10
  triggers: string[];
  diaryText?: string;
}

export interface ExerciseRecord {
  id: string; // exercise id
  timestamp: number;
  completed: boolean;
  rating?: number; // 1–5 stars, set after completion
  urgeBeforeStart?: number; // urge level when exercise was started
  moodBeforeStart?: number;
}

export interface UserLearningProfile {
  version: number;
  checkIns: CheckInRecord[];
  exerciseHistory: ExerciseRecord[];
  // Computed scores — updated on every save
  exerciseScores: Record<string, number>; // exercise id → affinity score (0–100)
  triggerFrequency: Record<string, number>; // trigger key → count
  avgMood7d: number;
  avgUrge7d: number;
  totalCheckIns: number;
  totalExercisesDone: number;
  streakDays: number;
  lastUpdated: number;
}

// ─── Storage key ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'bp_learning_profile_v1';

// ─── Default profile ──────────────────────────────────────────────────────────

function defaultProfile(): UserLearningProfile {
  return {
    version: 1,
    checkIns: [],
    exerciseHistory: [],
    exerciseScores: {},
    triggerFrequency: {},
    avgMood7d: 3,
    avgUrge7d: 3,
    totalCheckIns: 0,
    totalExercisesDone: 0,
    streakDays: 0,
    lastUpdated: Date.now(),
  };
}

// ─── Load / Save ──────────────────────────────────────────────────────────────

export function loadProfile(): UserLearningProfile {
  if (typeof window === 'undefined') return defaultProfile();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultProfile();
    const parsed = JSON.parse(raw) as UserLearningProfile;
    return { ...defaultProfile(), ...parsed };
  } catch {
    return defaultProfile();
  }
}

function saveProfile(profile: UserLearningProfile): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    // Storage quota exceeded — silently ignore
  }
}

// ─── Core algorithm helpers ───────────────────────────────────────────────────

/**
 * Compute the rolling 7-day average for mood and urge from recent check-ins.
 */
function computeRollingAverages(checkIns: CheckInRecord[]): { avgMood7d: number; avgUrge7d: number } {
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = checkIns.filter(c => c.timestamp >= cutoff);
  if (recent.length === 0) return { avgMood7d: 3, avgUrge7d: 3 };
  const avgMood7d = recent.reduce((s, c) => s + c.mood, 0) / recent.length;
  const avgUrge7d = recent.reduce((s, c) => s + c.urge, 0) / recent.length;
  return {
    avgMood7d: Math.round(avgMood7d * 10) / 10,
    avgUrge7d: Math.round(avgUrge7d * 10) / 10,
  };
}

/**
 * Compute trigger frequency across all check-ins.
 */
function computeTriggerFrequency(checkIns: CheckInRecord[]): Record<string, number> {
  const freq: Record<string, number> = {};
  for (const c of checkIns) {
    for (const t of c.triggers) {
      freq[t] = (freq[t] ?? 0) + 1;
    }
  }
  return freq;
}

/**
 * Compute streak: consecutive days with at least one check-in.
 */
function computeStreak(checkIns: CheckInRecord[]): number {
  if (checkIns.length === 0) return 0;
  const days = new Set(
    checkIns.map(c => {
      const d = new Date(c.timestamp);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    })
  );
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (days.has(key)) {
      streak++;
    } else if (i > 0) {
      break;
    }
  }
  return streak;
}

/**
 * The core personalisation scoring algorithm.
 *
 * For each exercise, a score (0–100) is computed based on:
 *  1. Completion history — exercises completed more often score higher
 *  2. User rating — highly-rated exercises score higher
 *  3. Urge-context match — if user has high urge, short coping exercises score higher
 *  4. Mood-context match — if mood is low, grounding/breathing exercises score higher
 *  5. Trigger-category match — e.g. "Anxiety" trigger → breathing exercises score higher
 *  6. Recency decay — recently completed exercises score slightly lower (variety)
 *  7. Category diversity — exercises from under-used categories score higher
 *
 * The algorithm is iterative: every new check-in or exercise completion updates the scores.
 */
export function computeExerciseScores(
  profile: UserLearningProfile,
  allExerciseIds: string[],
  exerciseMeta: Record<string, { category: string; duration: string }>
): Record<string, number> {
  const scores: Record<string, number> = {};
  const now = Date.now();
  const DAY_MS = 24 * 60 * 60 * 1000;

  // Build per-exercise stats
  const completionCount: Record<string, number> = {};
  const avgRating: Record<string, number> = {};
  const lastCompleted: Record<string, number> = {};
  const ratingSum: Record<string, number> = {};
  const ratingCount: Record<string, number> = {};

  for (const rec of profile.exerciseHistory) {
    if (!rec.completed) continue;
    completionCount[rec.id] = (completionCount[rec.id] ?? 0) + 1;
    if (rec.rating) {
      ratingSum[rec.id] = (ratingSum[rec.id] ?? 0) + rec.rating;
      ratingCount[rec.id] = (ratingCount[rec.id] ?? 0) + 1;
    }
    if (!lastCompleted[rec.id] || rec.timestamp > lastCompleted[rec.id]) {
      lastCompleted[rec.id] = rec.timestamp;
    }
  }

  for (const id of Object.keys(ratingSum)) {
    avgRating[id] = ratingSum[id] / ratingCount[id];
  }

  // Category usage counts
  const categoryUsage: Record<string, number> = {};
  for (const id of Object.keys(completionCount)) {
    const cat = exerciseMeta[id]?.category ?? 'Other';
    categoryUsage[cat] = (categoryUsage[cat] ?? 0) + completionCount[id];
  }

  // Top triggers
  const topTriggers = Object.entries(profile.triggerFrequency)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([k]) => k);

  // Trigger → category affinity map
  const TRIGGER_CATEGORY_MAP: Record<string, string[]> = {
    'trigger-anxiety': ['Breathing', 'Grounding', 'Mindfulness'],
    'trigger-stress': ['Breathing', 'Relaxation', 'Meditation'],
    'trigger-boredom': ['Movement', 'Mindfulness', 'Reflection'],
    'trigger-boredom-eve': ['Meditation', 'Reflection', 'Mindset'],
    'trigger-loneliness': ['Mindset', 'Reflection', 'Meditation'],
    'trigger-financial': ['CBT', 'Reflection', 'Coping'],
    'trigger-social': ['CBT', 'Coping', 'Mindset'],
    'trigger-celebration': ['Grounding', 'Coping', 'Mindfulness'],
    'trigger-sports': ['Grounding', 'Coping', 'Breathing'],
    'trigger-ads': ['Grounding', 'CBT', 'Coping'],
  };

  // Preferred categories from top triggers
  const preferredCategories = new Set<string>();
  for (const t of topTriggers) {
    for (const cat of TRIGGER_CATEGORY_MAP[t] ?? []) {
      preferredCategories.add(cat);
    }
  }

  for (const id of allExerciseIds) {
    let score = 50; // baseline

    const meta = exerciseMeta[id];
    const category = meta?.category ?? 'Other';
    const duration = meta?.duration ?? 'short';

    // 1. Completion history (max +20)
    const completions = completionCount[id] ?? 0;
    score += Math.min(completions * 5, 20);

    // 2. User rating (max +20)
    const rating = avgRating[id];
    if (rating !== undefined) {
      score += ((rating - 1) / 4) * 20; // 1→0, 5→20
    }

    // 3. Urge-context match
    if (profile.avgUrge7d >= 6) {
      // High urge → prefer short coping/breathing/grounding
      if (duration === 'short') score += 10;
      if (['Coping', 'Breathing', 'Grounding'].includes(category)) score += 8;
    } else if (profile.avgUrge7d <= 3) {
      // Low urge → prefer deeper reflection/meditation
      if (duration === 'long') score += 8;
      if (['Meditation', 'Reflection', 'CBT'].includes(category)) score += 6;
    }

    // 4. Mood-context match
    if (profile.avgMood7d <= 2) {
      // Low mood → grounding, breathing, self-compassion
      if (['Grounding', 'Breathing', 'Mindset'].includes(category)) score += 8;
    } else if (profile.avgMood7d >= 4) {
      // Good mood → deeper work
      if (['CBT', 'Reflection', 'Mindfulness'].includes(category)) score += 6;
    }

    // 5. Trigger-category match (max +15)
    if (preferredCategories.has(category)) {
      score += 15;
    }

    // 6. Recency decay — if completed in last 3 days, reduce score slightly
    const lastDone = lastCompleted[id];
    if (lastDone) {
      const daysSince = (now - lastDone) / DAY_MS;
      if (daysSince < 1) score -= 20; // done today
      else if (daysSince < 3) score -= 10;
    }

    // 7. Category diversity — under-used categories get a boost
    const catUsage = categoryUsage[category] ?? 0;
    if (catUsage === 0) score += 8; // never tried this category
    else if (catUsage <= 2) score += 4;

    // Clamp to 0–100
    scores[id] = Math.max(0, Math.min(100, Math.round(score)));
  }

  return scores;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Record a new check-in and recompute the learning profile.
 */
export function recordCheckIn(
  data: Omit<CheckInRecord, 'id' | 'timestamp'>
): UserLearningProfile {
  const profile = loadProfile();
  const record: CheckInRecord = {
    ...data,
    id: `ci-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: Date.now(),
  };
  profile.checkIns = [...profile.checkIns, record].slice(-365); // keep 1 year
  profile.triggerFrequency = computeTriggerFrequency(profile.checkIns);
  const avgs = computeRollingAverages(profile.checkIns);
  profile.avgMood7d = avgs.avgMood7d;
  profile.avgUrge7d = avgs.avgUrge7d;
  profile.totalCheckIns = profile.checkIns.length;
  profile.streakDays = computeStreak(profile.checkIns);
  profile.lastUpdated = Date.now();
  saveProfile(profile);
  return profile;
}

/**
 * Record an exercise completion (and optional rating) and recompute scores.
 */
export function recordExerciseCompletion(
  exerciseId: string,
  rating?: number,
  contextUrge?: number,
  contextMood?: number
): UserLearningProfile {
  const profile = loadProfile();
  const record: ExerciseRecord = {
    id: exerciseId,
    timestamp: Date.now(),
    completed: true,
    rating,
    urgeBeforeStart: contextUrge,
    moodBeforeStart: contextMood,
  };
  profile.exerciseHistory = [...profile.exerciseHistory, record].slice(-500);
  profile.totalExercisesDone = profile.exerciseHistory.filter(e => e.completed).length;
  profile.lastUpdated = Date.now();
  saveProfile(profile);
  return profile;
}

/**
 * Update the exercise scores in the profile (call after loading exercise metadata).
 */
export function refreshExerciseScores(
  exerciseMeta: Record<string, { category: string; duration: string }>
): UserLearningProfile {
  const profile = loadProfile();
  profile.exerciseScores = computeExerciseScores(
    profile,
    Object.keys(exerciseMeta),
    exerciseMeta
  );
  profile.lastUpdated = Date.now();
  saveProfile(profile);
  return profile;
}

/**
 * Get top N personalised exercise suggestions for a given duration bucket.
 * Returns exercise IDs sorted by affinity score descending.
 */
export function getPersonalisedSuggestions(
  duration: 'short' | 'medium' | 'long' | 'all',
  exerciseMeta: Record<string, { category: string; duration: string }>,
  topN = 4
): string[] {
  const profile = loadProfile();
  const scores = computeExerciseScores(profile, Object.keys(exerciseMeta), exerciseMeta);

  const candidates = Object.entries(scores)
    .filter(([id]) => {
      if (duration === 'all') return true;
      return exerciseMeta[id]?.duration === duration;
    })
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([id]) => id);

  return candidates;
}

/**
 * Get a human-readable reason why an exercise is being suggested.
 */
export function getSuggestionReason(
  exerciseId: string,
  exerciseMeta: Record<string, { category: string; duration: string }>
): string {
  const profile = loadProfile();
  const meta = exerciseMeta[exerciseId];
  if (!meta) return 'Recommended for you';

  const completions = profile.exerciseHistory.filter(
    e => e.id === exerciseId && e.completed
  ).length;
  const ratings = profile.exerciseHistory
    .filter(e => e.id === exerciseId && e.rating)
    .map(e => e.rating as number);
  const avgRating = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;

  if (avgRating >= 4) return `You rated this ${avgRating.toFixed(1)}★ — a favourite`;
  if (completions >= 3) return `You've done this ${completions} times`;
  if (profile.avgUrge7d >= 6 && meta.duration === 'short') return 'Quick relief for high urge days';
  if (profile.avgMood7d <= 2 && ['Grounding', 'Breathing'].includes(meta.category))
    return 'Helpful when mood is low';

  const topTriggers = Object.entries(profile.triggerFrequency)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([k]) => k);

  const TRIGGER_LABELS: Record<string, string> = {
    'trigger-anxiety': 'anxiety',
    'trigger-stress': 'work stress',
    'trigger-boredom': 'boredom',
    'trigger-boredom-eve': 'evening restlessness',
    'trigger-loneliness': 'loneliness',
    'trigger-financial': 'financial worry',
    'trigger-social': 'social pressure',
    'trigger-celebration': 'celebrations',
    'trigger-sports': 'watching sport',
    'trigger-ads': 'gambling ads',
  };

  if (topTriggers.length > 0) {
    const label = TRIGGER_LABELS[topTriggers[0]];
    if (label) return `Matches your ${label} pattern`;
  }

  if (completions === 0) return 'New — try something different';
  return 'Personalised for your journey';
}

/**
 * Get a summary insight string for the profile screen.
 */
export function getLearningInsights(profile: UserLearningProfile): string[] {
  const insights: string[] = [];

  if (profile.totalCheckIns < 3) {
    insights.push('Complete a few more check-ins to unlock personalised suggestions.');
    return insights;
  }

  const topTrigger = Object.entries(profile.triggerFrequency)
    .sort((a, b) => b[1] - a[1])[0];

  const TRIGGER_LABELS: Record<string, string> = {
    'trigger-anxiety': 'Anxiety',
    'trigger-stress': 'Work stress',
    'trigger-boredom': 'Boredom',
    'trigger-boredom-eve': 'Evening restlessness',
    'trigger-loneliness': 'Loneliness',
    'trigger-financial': 'Financial worry',
    'trigger-social': 'Social pressure',
    'trigger-celebration': 'Celebrations',
    'trigger-sports': 'Watching sport',
    'trigger-ads': 'Gambling ads',
  };

  if (topTrigger) {
    insights.push(
      `Your most common trigger is "${TRIGGER_LABELS[topTrigger[0]] ?? topTrigger[0]}" (${topTrigger[1]} times). Exercises are tailored to help with this.`
    );
  }

  if (profile.avgUrge7d >= 6) {
    insights.push('Your urge levels have been high this week — short coping exercises are prioritised for you.');
  } else if (profile.avgUrge7d <= 3) {
    insights.push('Your urge levels are low this week — great work. Deeper reflection exercises are now suggested.');
  }

  if (profile.avgMood7d >= 4) {
    insights.push('Your mood has been positive this week. Keep building on this momentum.');
  } else if (profile.avgMood7d <= 2) {
    insights.push('Your mood has been lower this week. Grounding and breathing exercises are prioritised.');
  }

  const topExercise = Object.entries(
    profile.exerciseHistory
      .filter(e => e.completed)
      .reduce((acc, e) => {
        acc[e.id] = (acc[e.id] ?? 0) + 1;
        return acc;
      }, {} as Record<string, number>)
  ).sort((a, b) => b[1] - a[1])[0];

  if (topExercise && topExercise[1] >= 2) {
    insights.push(`You've completed exercises ${profile.totalExercisesDone} times. The algorithm is learning your preferences.`);
  }

  return insights.slice(0, 3);
}
